import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Paper,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Notes as NotesIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSessionState } from '../hooks/useSessionState';
import PlaygroundContent from '../components/playground/PlaygroundContent';
import PlaygroundNotes from '../components/playground/PlaygroundNotes';
import PlaygroundQuiz from '../components/playground/PlaygroundQuiz';

const CoursePlaygroundPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const id = Number(courseId);

  const { sessionState, loading, error } = useSessionState(id);
  const [notesOpen, setNotesOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const prevQuizSessionId = useRef<number | null>(null);

  useEffect(() => {
    const quizId = sessionState?.activeQuiz?.quizSessionId ?? null;
    if (quizId && quizId !== prevQuizSessionId.current) {
      setQuizOpen(true);
      prevQuizSessionId.current = quizId;
    }
    if (!quizId) {
      prevQuizSessionId.current = null;
    }
  }, [sessionState?.activeQuiz?.quizSessionId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/courses/${id}`)} sx={{ mt: 2 }}>
          Back to Course
        </Button>
      </Container>
    );
  }

  if (!sessionState) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">No active session for this course right now.</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/courses/${id}`)} sx={{ mt: 2 }}>
          Back to Course
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <IconButton size="small" onClick={() => navigate(`/courses/${id}`)}>
          <BackIcon />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          Course Playground
          {sessionState.status === 'ACTIVE' && (
            <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
              ● LIVE
            </Typography>
          )}
          {sessionState.status === 'ENDED' && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Session ended
            </Typography>
          )}
        </Typography>
        {sessionState?.activeQuiz && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => setQuizOpen(true)}
            title="Live Quiz"
          >
            <QuizIcon />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={() => setNotesOpen((v) => !v)}
          title="My Notes"
        >
          <NotesIcon />
          {notesOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <PlaygroundContent sessionState={sessionState} courseId={id} />
        </Box>

        {/* Notes panel */}
        <Collapse in={notesOpen} orientation="horizontal">
          <Paper
            elevation={0}
            sx={{
              width: 300,
              height: '100%',
              borderLeft: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PlaygroundNotes sessionId={sessionState.sessionId} />
          </Paper>
        </Collapse>
      </Box>

      {/* Live Quiz Dialog */}
      {sessionState.activeQuiz && (
        <Dialog
          open={quizOpen}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogContent sx={{ p: 0 }}>
            <PlaygroundQuiz
              quiz={sessionState.activeQuiz}
              onDismiss={() => setQuizOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default CoursePlaygroundPage;
